import {
  CSSProperties,
  ChangeEvent,
  FormEvent,
  KeyboardEvent,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import { ChatContext } from '@app/context'
import { getContextEntity } from '@app/helpers'
import {
  useAppDispatch,
  useConversationActorRole,
  useConversation,
  useIsDeveloper,
  useModal,
  usePublishedListings,
  usePublishedProjects,
} from '@app/hooks'
import { trackReplySelectModalOpened } from '@app/services/tracking'
import { getPublishedListingsTotalThunk } from '@app/store/publishedListings'
import { getPublishedProjectsTotalThunk } from '@app/store/publishedProjects'
import { isB2BView } from '@app/utils'

import { Icon, Modal, TRANSACTION_TYPE_QUERY } from '@lib/components'
import type { TransactionType } from '@lib/components'

import { AttachmentsCarousel } from './AttachmentsCarousel'
import { SelectListingContainer } from './SelectEntityContainer/SelectListingContainer'
import { SelectProjectContainer } from './SelectEntityContainer/SelectProjectContainer'
import {
  DEFAULT_TEXTAREA_HEIGHT,
  MAX_REPLY_LINES,
  MAX_TEXTAREA_HEIGHT,
  MAX_TEXTAREA_LENGTH,
} from './constants'
import styles from './styles.module.scss'
import { ReplyProps } from './types'
import useReply from './useReply'
import { useReplyAttachments } from './useReplyAttachments'
import { useSelectEntityFilters } from './useSelectEntityFilters'

const TRANSACTION_TYPE_BY_ID: Partial<Record<string, TransactionType>> = {
  [TRANSACTION_TYPE_QUERY.buy]: 'buy',
  [TRANSACTION_TYPE_QUERY.rent]: 'rent',
}

const parsePx = (value: string) => {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

export const Reply = ({
  conversationId,
  inputValue,
  setInputValue,
  attachments,
  setAttachments,
  activeFollowUpAction,
}: ReplyProps) => {
  const { t } = useTranslation()
  const { state } = use(ChatContext)
  const dispatch = useAppDispatch()
  const conversation = useConversation()
  const conversationActorRole = useConversationActorRole()
  const {
    total: publishedListingsTotal,
    totalAvailable: publishedListingsAvailableTotal,
    totalLoaded: listingsTotalLoaded,
    totalFetching: listingsTotalFetching,
  } = usePublishedListings()
  const {
    total: publishedProjectsTotal,
    totalAvailable: publishedProjectsAvailableTotal,
    totalLoaded: projectsTotalLoaded,
    totalFetching: projectsTotalFetching,
  } = usePublishedProjects()
  const isDeveloper = useIsDeveloper()
  const listing =
    (conversation && getContextEntity(state.listings, conversation)) || null
  const isBackOffice = isB2BView()

  const conversationListingTransactionType = useMemo(() => {
    const transactionTypeId = listing?.transactionType?.id

    if (!transactionTypeId) {
      return undefined
    }

    return TRANSACTION_TYPE_BY_ID[transactionTypeId]
  }, [listing?.transactionType?.id])

  const {
    listingFilters,
    projectFilters,
    handleListingSearchFilterChange,
    handleListingTransactionFilterChange,
    handleListingSortFilterChange,
    handleProjectSearchFilterChange,
    handleProjectTransactionFilterChange,
    handleProjectSortFilterChange,
  } = useSelectEntityFilters(conversationId, conversationListingTransactionType)

  useEffect(() => {
    if (!isBackOffice) {
      return
    }

    if (isDeveloper) {
      if (!projectsTotalLoaded && !projectsTotalFetching) {
        dispatch(getPublishedProjectsTotalThunk())
      }
      return
    }

    if (!listingsTotalLoaded && !listingsTotalFetching) {
      dispatch(getPublishedListingsTotalThunk())
    }
  }, [
    dispatch,
    isBackOffice,
    isDeveloper,
    listingsTotalLoaded,
    listingsTotalFetching,
    projectsTotalLoaded,
    projectsTotalFetching,
  ])

  const formRef = useRef<HTMLFormElement | null>(null)
  const { handleSubmit, handleChange } = useReply(
    inputValue,
    setInputValue,
    attachments,
    setAttachments,
    isDeveloper,
    activeFollowUpAction
  )

  const input = inputValue.get(conversationId) || ''
  const hasMessageContent = input.trim().length > 0
  const attachmentsForConversation = useMemo(
    () => attachments.get(conversationId) ?? [],
    [attachments, conversationId]
  )
  const selectedAttachmentIds = useMemo(
    () => attachmentsForConversation.map((attachment) => attachment.id),
    [attachmentsForConversation]
  )
  const hasAttachments = attachmentsForConversation.length > 0
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [textareaHeight, setTextareaHeight] = useState(DEFAULT_TEXTAREA_HEIGHT)

  const adjustTextareaHeight = useCallback(
    (element?: HTMLTextAreaElement | null) => {
      const target = element ?? textareaRef.current

      if (!target) {
        setTextareaHeight((prev) =>
          prev === DEFAULT_TEXTAREA_HEIGHT ? prev : DEFAULT_TEXTAREA_HEIGHT
        )
        return
      }

      target.style.height = `${DEFAULT_TEXTAREA_HEIGHT}px`

      const styles = window.getComputedStyle(target)
      const lineHeight =
        parsePx(styles.lineHeight) ||
        parsePx(styles.fontSize) ||
        DEFAULT_TEXTAREA_HEIGHT
      const paddingY =
        parsePx(styles.paddingTop) + parsePx(styles.paddingBottom)
      const borderY =
        parsePx(styles.borderTopWidth) + parsePx(styles.borderBottomWidth)

      const computedMaxHeight =
        lineHeight * MAX_REPLY_LINES + paddingY + borderY
      const maxHeight =
        computedMaxHeight > 0 ? computedMaxHeight : MAX_TEXTAREA_HEIGHT
      const effectiveMaxHeight = Math.max(maxHeight, DEFAULT_TEXTAREA_HEIGHT)

      target.style.maxHeight = `${effectiveMaxHeight}px`

      const rawHeight = target.scrollHeight - DEFAULT_TEXTAREA_HEIGHT
      const desiredHeight = Math.max(rawHeight, DEFAULT_TEXTAREA_HEIGHT)
      const clampedHeight = Math.min(desiredHeight, effectiveMaxHeight)
      target.style.height = `${clampedHeight}px`

      if (rawHeight > effectiveMaxHeight) {
        target.style.overflowY = 'auto'
        target.scrollTop = target.scrollHeight
      } else {
        target.style.overflowY = 'hidden'
      }

      setTextareaHeight((prev) => (prev === clampedHeight ? prev : clampedHeight))
    },
    [setTextareaHeight]
  )

  useEffect(() => {
    adjustTextareaHeight()
  }, [adjustTextareaHeight, conversationId, input])

  const onKeyDownHandler = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) {
      return
    }

    const formElement = formRef.current
    if (!formElement || (!hasMessageContent && !hasAttachments)) {
      return
    }

    event.preventDefault()
    formElement.requestSubmit()
  }

  const changeHandler = useMemo(
    () => handleChange(conversationId),
    [conversationId, handleChange]
  )

  const submitHandler = useMemo(
    () => handleSubmit(conversationId),
    [conversationId, handleSubmit]
  )

  const onTextareaChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      changeHandler(event)
      adjustTextareaHeight(event.target)
    },
    [adjustTextareaHeight, changeHandler]
  )

  const onFormSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      const didSend = submitHandler(event)

      if (didSend) {
        window.setTimeout(() => {
          adjustTextareaHeight()
        }, 0)
      }
    },
    [adjustTextareaHeight, submitHandler]
  )

  const isTextareaExpanded = textareaHeight > DEFAULT_TEXTAREA_HEIGHT
  const replyButtonBottomOffset = isTextareaExpanded ? 8 : 12.5
  const replyFormStyle = useMemo<CSSProperties>(
    () =>
      ({
        '--reply-button-bottom': `${replyButtonBottomOffset}px`,
      } as CSSProperties),
    [replyButtonBottomOffset]
  )

  const selectModal = useModal('selectModal')
  
  const closeModal = useCallback(() => {
    selectModal.close()
  }, [selectModal])

  const openModal = useCallback(() => {
    selectModal.open()

    const filters = isDeveloper ? projectFilters : listingFilters

    trackReplySelectModalOpened(conversationId, {
      listing,
      transactionType: filters.transactionType ?? null,
      sort: filters.sort,
    }, conversationActorRole)
  }, [
    conversationActorRole,
    conversationId,
    isDeveloper,
    listing,
    listingFilters,
    projectFilters,
    selectModal,
  ])

  const { handleSelect, handleAttachmentRemove } = useReplyAttachments({
    conversationId,
    setAttachments,
    closeModal,
  })

  const totalsLoaded = isDeveloper ? projectsTotalLoaded : listingsTotalLoaded
  const hasPublishedEntities = isDeveloper
    ? (publishedProjectsAvailableTotal ?? publishedProjectsTotal ?? 0) > 0
    : (publishedListingsAvailableTotal ?? publishedListingsTotal ?? 0) > 0
  const showPlusButton = isBackOffice && totalsLoaded && hasPublishedEntities
  const totalsIsNotLoaded = isBackOffice && !totalsLoaded

  if (totalsIsNotLoaded) {
    return null
  }
  
  return (
    <>
      <div className={styles.replyMessageContainer}>
        {hasAttachments && (
          <AttachmentsCarousel
            attachments={attachmentsForConversation}
            onRemove={handleAttachmentRemove}
            containerClassName={styles.attachmentsArea}
            dragEnabledClassName={styles.attachmentsAreaDragEnabled}
          />
        )}
        <form
          className={classNames(styles.reply, {
            [styles.replyNoButton]: !showPlusButton,
          })}
          onSubmit={onFormSubmit}
          ref={formRef}
          style={replyFormStyle}
        >
          {showPlusButton && (
            <button type="button" onClick={openModal}>
              <Icon path="icons/icon-plus-bold" />
            </button>
          )}
  
          <textarea
            placeholder={t('Chat.Reply.Placeholder')}
            value={input}
            onChange={onTextareaChange}
            onKeyDown={onKeyDownHandler}
            maxLength={MAX_TEXTAREA_LENGTH}
            style={{ height: `${textareaHeight}px` }}
            ref={textareaRef}
          ></textarea>      
          {(hasMessageContent || hasAttachments) && (
            <button type="submit">
              <Icon path="icons/icon-arrow-up" />
            </button>        
          )}
        </form>    
      </div>      
      <Modal
        isOpen={selectModal.isOpen}
        onClose={closeModal}
        title={isDeveloper ? t('Chat.Reply.Select.Project.Header') : t('Chat.Reply.Select.Listing.Header')}
        showBottomCloseButton={false}
      > 
        {isDeveloper ? (
          <SelectProjectContainer
            onSelect={handleSelect}
            searchQuery={projectFilters.searchQuery}
            onSearchChange={handleProjectSearchFilterChange}
            transactionType={projectFilters.transactionType}
            onTransactionTypeChange={handleProjectTransactionFilterChange}
            sort={projectFilters.sort}
            onSortChange={handleProjectSortFilterChange}
            selectedIds={selectedAttachmentIds}
          />
        ) : (
          <SelectListingContainer
            onSelect={handleSelect}
            searchQuery={listingFilters.searchQuery}
            onSearchChange={handleListingSearchFilterChange}
            transactionType={listingFilters.transactionType}
            onTransactionTypeChange={handleListingTransactionFilterChange}
            sort={listingFilters.sort}
            onSortChange={handleListingSortFilterChange}
            selectedIds={selectedAttachmentIds}
          />
        )}
      </Modal>
    </>
  )
}
